import { useCallback, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { coverImage, recordPhotoCount } from "@/lib/record-images";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { PhotoCountBadge } from "@/components/common/photo-count-badge/photo-count-badge";
import { RecordSyncBadge } from "@/components/records/record-sync-badge/record-sync-badge";
import { hasCachedAsset } from "@/offline/blobs";
import { thumbAssetKey } from "@/offline/asset-keys";
import { useJustSynced } from "@/hooks/use-just-synced";
import { useLongPress } from "@/hooks/use-long-press";
import type { PendingRecordTile } from "@/hooks/use-pending-records";
import type { ProjectRecord } from "@/types/api";

interface PhotoGridItemBaseProps {
  onClick?: (record: ProjectRecord) => void;
  /** Whether selection mode is active. Drives the indicator overlay. */
  isSelectionMode?: boolean;
  /** Whether this record is currently selected (for indicator state). */
  isSelected?: boolean;
  /**
   * Called when a long-press fires outside of selection mode. Wires the
   * "long-press to enter selection mode" entry per [[photo-grid-item.spec]]
   * US-002 and [[selection-mode.spec]] US-001.
   */
  onLongPress?: (record: ProjectRecord) => void;
  /**
   * Whether this record is the one open in the record side panel beside the
   * gallery — marked so you can see which photo the panel is showing. See
   * [[records-view.spec]] US-006.
   */
  isActive?: boolean;
}

interface PhotoGridItemProps extends PhotoGridItemBaseProps {
  record: ProjectRecord;
  /**
   * When set, this tile renders an outbox record (a local draft that has
   * not yet synced) instead of a server record: the thumbnail is drawn from
   * the locally captured blob and a per-tile sync-state badge is shown. See
   * [[offline-record-outbox]] and [[photo-grid-item.spec]] § Outbox sync badge.
   */
  outbox?: PendingRecordTile;
  /**
   * Tap-to-retry handler for a `failed` outbox tile. The grid wires this to
   * reset the record to `pending` and re-fire the background sync. Required
   * whenever `outbox.status === "failed"` for the retry tap to do anything.
   */
  onRetryOutbox?: (tile: PendingRecordTile) => void;
  /**
   * Append-outbox sync state for a **synced** server record that has "+ Add"
   * photos still uploading (see [[append-photo-outbox]]). `"pending"` shows the
   * same connectivity-aware "Syncing"/"Queued" badge a creating record shows;
   * `"failed"` shows an error badge. Unlike the `outbox` (create) prop, this
   * keeps the tile fully **tappable** and never dimmed/inert — the record is
   * real, so tapping opens the modal, and append retry lives there, not on the
   * tile. Ignored when `outbox` is set (a tile is never both at once).
   */
  appendState?: "pending" | "failed";
}

export function PhotoGridItem({
  record,
  outbox,
  onRetryOutbox,
  appendState,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onLongPress,
  isActive = false,
}: PhotoGridItemProps) {
  // A server-record tile whose thumbnail is already in the in-memory image
  // cache (e.g. just synced — the outbox seed warmed it) starts `loaded` so it
  // paints at full opacity on the first frame, with no muted placeholder or
  // fade. An outbox tile, or an uncached server tile, starts `false` and fades
  // in on load as usual. See [[photo-grid-item.spec]] / [[offline-asset-cache]].
  const [loaded, setLoaded] = useState(() =>
    outbox ? false : hasCachedAsset(thumbAssetKey(record)),
  );

  // The tile thumbnail is the record's cover — `images[0]` under the v2.0
  // read, the legacy `image` otherwise — resolved at read time via the
  // [[record-images]] seam; the tile never relies on a top-level `image`.
  const cover = coverImage(record);
  // `?? ""` keeps the prop `string`: `OfflineImage` renders no `src` for an
  // empty string (its offline-degradation path), the right behavior for a
  // record with no cover photo.
  const src = cover?.thumbnail ?? cover?.image ?? "";
  // A record holding more than one photo shows a small count badge (below).
  const photoCount = recordPhotoCount(record);
  // Create-outbox state: only a *creating* (unsynced) tile shows a sync badge
  // and, once failed, taps to retry — its server id isn't placed yet.
  const isFailed = outbox?.status === "failed";
  const isPending = outbox?.status === "pending";

  // Append-outbox state for a SYNCED record with "+ Add" photos uploading. It
  // surfaces the SAME badges as a create (below), but never reroutes the tap
  // to retry — the record is real, so it opens like any synced tile and
  // append retry lives in the viewer. A tile is never both a create and an
  // append at once, so `appendState` only matters when there is no `outbox`.
  // See [[append-photo-outbox]].
  const isAppendPending = !outbox && appendState === "pending";
  const isAppendFailed = !outbox && appendState === "failed";

  // Whether to render each badge — true for a creating tile OR a synced tile
  // with queued/failed appends. The two share badge markup; they differ only in
  // tap behavior, handled separately.
  const showPendingBadge = isPending || isAppendPending;
  const showFailedBadge = isFailed || isAppendFailed;

  // True for ~1.5s right after this record's create syncs — drives the
  // transient "Synced" confirmation badge. See [[offline-record-outbox]].
  const isJustSynced = useJustSynced(record.id);

  // Long-press to enter selection mode — shared with the list row so the
  // gesture is identical on both. See [[photo-grid-item.spec]] § US-002.
  const { longPressHandlers, consumeClick } = useLongPress(record, onLongPress);

  const handleClick = useCallback(() => {
    // If a long-press just fired, suppress the click that follows on touch
    // devices so the user doesn't get both a "select this record" and an
    // "open this record" event on the same press.
    if (consumeClick()) return;
    // A failed outbox tile taps to retry — not to open the modal. Selection
    // mode still wins (long-press selection is unaffected by outbox state).
    if (isFailed && !isSelectionMode && outbox) {
      onRetryOutbox?.(outbox);
      return;
    }
    // A pending tile opens the viewer whether it reads "Syncing" (online,
    // mid-upload) or "Queued" (offline). The viewer is what keeps a mid-upload
    // draft safe: it locks every action that would hit the temporary
    // clientUuid (a PATCH would 404). See [[offline-record-outbox]] and
    // [[image-viewer-modal.spec]].
    onClick?.(record);
  }, [
    consumeClick,
    isFailed,
    isSelectionMode,
    outbox,
    onRetryOutbox,
    onClick,
    record,
  ]);

  // The open record (side panel) and a selected record (select tool) share one
  // highlight, which rules out the hover fill below.
  const isHighlighted = isActive || (isSelectionMode && isSelected);

  return (
    <button
      type="button"
      data-testid="record-tile"
      onClick={handleClick}
      {...longPressHandlers}
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        "group relative block aspect-square w-full overflow-hidden bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
      style={{ WebkitTouchCallout: "none" }}
      aria-pressed={isSelectionMode ? isSelected : undefined}
      aria-current={isActive || undefined}
    >
      {outbox ? (
        // Outbox tile: the thumbnail is the locally captured blob. A plain
        // <img> off the in-hand object URL — no server URL, no
        // offline-image-cache — so it renders fully offline.
        <img
          src={outbox.thumbnailUrl}
          alt=""
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={cn(
            "h-full w-full object-cover transition-opacity duration-500",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      ) : (
        <OfflineImage
          assetKey={thumbAssetKey(record)}
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={cn(
            "h-full w-full object-cover transition-opacity duration-500",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
      {/* The open record (side panel) and a selected record (select tool)
          share one treatment: an inset ring, so it never spills into the grid
          gap, over a 15% tertiary tint. Painted right after the image and
          before the badges and selection check, so they stay on top.
          See [[photo-grid-item.spec]]. */}
      {isHighlighted ? (
        <span
          aria-hidden="true"
          data-testid="record-tile-highlight"
          className="pointer-events-none absolute inset-0 bg-tertiary/15 ring-[3px] ring-inset ring-tertiary"
        />
      ) : (
        // Hover fill: a fixed black scrim, so it reads the same in both themes
        // ([[theming]] § scrims over images). Same slot as the highlight, which
        // already marks a highlighted tile. `hover:` only applies where the
        // device can hover, so a tap on touch leaves nothing stuck. See
        // [[photo-grid-item.spec]] § Hover.
        <span
          aria-hidden="true"
          data-testid="record-tile-hover"
          className="pointer-events-none absolute inset-0 bg-black/15 opacity-0 transition-opacity duration-100 group-hover:opacity-100"
        />
      )}
      {/* Multi-photo records show a small stacked-photos count badge in the
          top-left corner; a single photo shows none. Unobtrusive and
          independent of the outbox sync badge (bottom-left), so the two
          coexist. See [[photo-grid-item.spec]] US-004,
          [[photo-count-badge.spec]] and [[multi-photo-records]]. */}
      <PhotoCountBadge count={photoCount} />
      {showPendingBadge && (
        <RecordSyncBadge state="pending" className="absolute bottom-1 left-1" />
      )}
      {showFailedBadge && (
        <RecordSyncBadge
          state="failed"
          isAppend={isAppendFailed}
          className="absolute bottom-1 left-1"
        />
      )}
      {isJustSynced && (
        <RecordSyncBadge state="synced" className="absolute bottom-1 left-1" />
      )}
      {isSelectionMode && (
        <div className="pointer-events-none absolute right-2 top-2 z-10">
          {isSelected ? (
            // Tertiary (accent) bg with white border per [[selection-mode.spec]]
            // § Layout — matches plan-viewer pins and tag-selector pills.
            <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-tertiary text-tertiary-foreground shadow-sm">
              <Check className="h-4 w-4" />
            </div>
          ) : (
            <div
              className="h-6 w-6 rounded-full border-2"
              style={{
                backgroundColor: "rgba(43, 43, 43, 0.4)",
                borderColor: "rgba(255, 255, 255, 0.9)",
              }}
            />
          )}
        </div>
      )}
    </button>
  );
}

export function PhotoGridItemSkeleton() {
  return <div className="aspect-square w-full animate-pulse bg-muted" />;
}
